import React, { useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import {
  ChangeProductsFiltersFunc,
  FetchProductsCacheKey,
  FetchProductsFilterFields,
  FetchProductsFilterProducts,
  FetchProductsFilters,
  FetchProductsLimit,
  FetchProductsPage,
  FetchProductsSort,
  FetchProductsSortTypes,
  OnProductSimilarSearchAction,
  ProductCategoryID,
  ProductNanoID,
  UpdateProductCacheAction
} from '../../../../products/productsTypes';
import { TeamNanoID, TeamUUID } from '../../../../teams/teamsTypes';
import {
  ErrorMessage,
  ID,
  FetchItemsLoadMoreItems,
  FetchItemsSort,
  IdFilterType,
  IsFetched
} from '../../../../../types';
import {
  FetchProductsSetsCacheKeys,
  UpdateProductsSetCacheAction
} from '../../../../productsSets/productsSetsTypes';
import { LifestyleDetectionParams } from '../../../../lifestyleDetections/lifestyleDetectionsTypes';
import { FetchLifestyleDetection } from '../../../../lifestyleDetections/queries/lifestyleDetectionsByImageUrl.query';
import { ThreeDStockContentSearchInImageView } from '../../headers/ThreeDStockContentSearchInImage/ThreeDStockContentSearchInImageTypes';

import { useThreeDStockContentView } from '../../../hooks/useThreeDStockContentView';
import { useThreeDStockContentScale } from '../../../hooks/useThreeDStockContentScale';
import { useThreeDStockProductsContentSortButtons } from './hooks/useThreeDStockProductsContentSortButtons';
import { useThreeDStockProductsContentCheckedCategories } from './hooks/useThreeDStockProductsContentCheckedCategories';
import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useReactQueryState } from '../../../../common/reactQuery/hooks/useReactQueryState';

import { ThreeDStockContentHeader } from '../../headers/ThreeDStockContentHeader';
import { ThreeDStockContentSortAndView } from '../../headers/ThreeDStockContentSortAndView';
import {
  ThreeDStockProductsList,
  ThreeDStockProductsListProduct,
  ThreeDStockProductsListProducts,
  ThreeDStockProductsListProductsSet
} from '../../lists/ThreeDStockProductsList';
import { ThreeDStockProductsCompactList } from '../../lists/ThreeDStockProductsCompactList';
import { ThreeDStockProductsFilters } from '../../filters/ThreeDStockProductsFilters';
import { ThreeDStockContentSearchInImage } from '../../headers/ThreeDStockContentSearchInImage';
import { ThreeDStockAllObjectsProductsContent } from '../ThreeDStockAllObjectsProductsContent';

import { LoadingGridSkeleton } from '../../../../../helpers/LoadingGridSkeleton';

import { isEveryPropEmpty } from '../../../../../utils/isEveryPropEmpty';

import { StockTabs } from '../../../stockConstants';
import { ProductsPermissions } from '../../../../products/productsConstants';
import { LifestyleDetectionCache } from '../../../../lifestyleDetections/LifestyleDetectionCache';

import { words } from '../../../../../locales/keys';

const productsSortI18nTextTypes = {
  [FetchProductsSortTypes.CREATED_AT_DESC]: words.newest,
  [FetchProductsSortTypes.SELECTED_PRODUCTS_COUNT_DESC]: words.mostUsed,
  [FetchProductsSortTypes.USERS_CHOSEN_PRODUCTS_COUNT_DESC]: words.mostLiked,
  [FetchProductsSortTypes.SCORE_DESC]: words.featured
};

const allFilterFields = [
  FetchProductsFilterFields.AI_MATERIAL_TAGS,
  FetchProductsFilterFields.AI_INTERIOR_STYLE_TAGS,
  FetchProductsFilterFields.AI_ROOM_CLASSIFICATION_TAGS,
  FetchProductsFilterFields.AI_FEATURE_TAGS,
  FetchProductsFilterFields.AI_MAIN_COLOR_TAGS,
  FetchProductsFilterFields.BLOCKED,
  FetchProductsFilterFields.RENDER_TYPE_IDS,
  FetchProductsFilterFields.FAVORITE,
  FetchProductsFilterFields.CURRENT_TEAM_FAVORITE
];

const tab = StockTabs.PRODUCTS;

interface ThreeDStockProductsContentProps {
  teamUuid?: TeamUUID;
  productsCacheKey: FetchProductsCacheKey;
  productsFilters: FetchProductsFilters;
  initialFilters: FetchProductsFilters;
  defaultProductCategoryId?: IdFilterType;
  defaultProductParentCategoryIds?: ProductCategoryID[];
  changeProductsFilters: ChangeProductsFiltersFunc;
  filterProducts: FetchProductsFilterProducts;
  productsSort: FetchItemsSort;
  sortProducts: (nextSort: FetchProductsSort) => void;
  products: ThreeDStockProductsListProducts;
  onProductEditButtonMouseEnter?: (productNanoId: ProductNanoID) => void;
  productsError: ErrorMessage;
  productsFetched: IsFetched;
  productsIsPlaceholderData: boolean;
  productsTotalCount: number;
  updateProductCache?: UpdateProductCacheAction<ThreeDStockProductsListProduct>;
  hasNextProductsPage: boolean;
  productsPage: FetchProductsPage;
  productsLimit: FetchProductsLimit;
  loadMoreProducts: FetchItemsLoadMoreItems;
  productsFetchingNextPage: boolean;
  productsSet: ThreeDStockProductsListProductsSet | null;
  productsSetCacheKeys?: FetchProductsSetsCacheKeys;
  updateProductsSetCache: UpdateProductsSetCacheAction<ThreeDStockProductsListProductsSet>;
  onProductSimilarSearchFilter: OnProductSimilarSearchAction;
  changeProductsCategoryFilters: (
    tab?: StockTabs,
    changeFilters?: FetchProductsFilters,
    removeFilters?: string[]
  ) => void;
  clearProductsFilters?: (
    persistFiltersFields?: FetchProductsFilterFields[]
  ) => void;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  withTypeFiler?: boolean;
  withSeasonFilter?: boolean;
  withSearchInImage?: boolean;
  isModal?: boolean;
}

function ThreeDStockProductsContent({
  teamUuid,
  productsCacheKey,
  productsFilters,
  initialFilters,
  defaultProductCategoryId,
  defaultProductParentCategoryIds,
  changeProductsFilters,
  filterProducts,
  productsSort,
  sortProducts,
  products,
  onProductEditButtonMouseEnter,
  productsError,
  productsFetched,
  productsIsPlaceholderData,
  productsTotalCount,
  updateProductCache,
  hasNextProductsPage,
  productsPage,
  productsLimit,
  loadMoreProducts,
  productsFetchingNextPage,
  productsSet,
  productsSetCacheKeys,
  updateProductsSetCache,
  onProductSimilarSearchFilter,
  changeProductsCategoryFilters,
  clearProductsFilters,
  companyNanoId,
  isMyLibrary,
  withSeasonFilter,
  withTypeFiler,
  withSearchInImage,
  isModal
}: ThreeDStockProductsContentProps) {
  const currentUser = useCurrentUser();

  const [showFilters, setShowFilters] = useState(true);
  const [activeDetectionId, setActiveDetectionId] = useState<ID | null>(null);
  const [searchInImageView, setSearchInImageView] =
    useState<ThreeDStockContentSearchInImageView>('single');
  const [imageDetections, setImageDetections] = useState<{
    imageUrl: string;
    detections: FetchLifestyleDetection[];
  } | null>(null);
  const { value: searchInImageParams } =
    useReactQueryState<LifestyleDetectionParams>(
      LifestyleDetectionCache.searchImageUrlCacheKey()
    );
  const isSearchingInImage = !!(
    withSearchInImage && searchInImageParams?.imageUrl
  );
  const detections =
    imageDetections?.imageUrl === searchInImageParams?.imageUrl
      ? imageDetections?.detections
      : undefined;

  const handleDetectionsChange = useCallback(
    (detections: FetchLifestyleDetection[]) => {
      setImageDetections((previous) => {
        const imageUrl = searchInImageParams?.imageUrl;
        if (
          previous?.imageUrl === imageUrl &&
          isEqual(previous?.detections, detections)
        ) {
          return previous;
        }

        return { imageUrl, detections };
      });
    },
    [searchInImageParams?.imageUrl]
  );

  useEffect(() => {
    setSearchInImageView('single');
  }, [searchInImageParams?.imageUrl, withSearchInImage]);

  useEffect(() => {
    setActiveDetectionId(null);
  }, [searchInImageParams?.imageUrl, searchInImageView, detections]);

  const { checkedCategories, setCheckedCategories } =
    useThreeDStockProductsContentCheckedCategories();

  const closeFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const openFilters = useCallback(() => {
    setShowFilters(true);
  }, []);

  const withFilters = useMemo<boolean>(
    () =>
      !isEveryPropEmpty(pick(productsFilters, allFilterFields)) ||
      !isEmpty(checkedCategories),
    [productsFilters, checkedCategories]
  );

  const { isGridView } = useThreeDStockContentView({ tab });
  const { scale } = useThreeDStockContentScale({ tab });

  const handleChangeCategory = useCallback<
    (changeFilters?: FetchProductsFilters, removeFilters?: string[]) => void
  >(
    (changeFilters, removeFilters) => {
      changeProductsCategoryFilters(
        StockTabs.PRODUCTS,
        changeFilters,
        removeFilters
      );
    },
    [changeProductsCategoryFilters]
  );

  const { sortButtons, sortBy } = useThreeDStockProductsContentSortButtons({
    productsBaseCacheKey: productsCacheKey,
    sortProducts,
    productsFilters,
    productsSort
  });

  let downloadButtonAction = ProductsPermissions.READ_PRODUCT_DOWNLOAD_BUTTON;
  let downloadSeparatelyButtonAction =
    ProductsPermissions.READ_PRODUCT_DOWNLOAD_SEPARATELY_BUTTON;

  let findSimilarButtonAction =
    ProductsPermissions.READ_PRODUCT_FIND_SIMILAR_FILTER_BUTTON;
  let editButtonAction = ProductsPermissions.READ_PRODUCT_EDIT_BUTTON;

  if (isMyLibrary) {
    findSimilarButtonAction =
      ProductsPermissions.READ_SELF_COMPANY_LIBRARY_PRODUCT_FIND_SIMILAR_BUTTON;
    downloadButtonAction =
      ProductsPermissions.READ_SELF_COMPANY_LIBRARY_PRODUCT_DOWNLOAD_BUTTON;
    downloadSeparatelyButtonAction =
      ProductsPermissions.READ_SELF_COMPANY_LIBRARY_PRODUCT_DOWNLOAD_SEPARATELY_BUTTON;
    editButtonAction =
      ProductsPermissions.READ_SELF_COMPANY_EDIT_PRODUCT_BUTTON;
  } else if (companyNanoId) {
    findSimilarButtonAction =
      ProductsPermissions.READ_OTHER_COMPANY_LIBRARY_PRODUCT_FIND_SIMILAR_BUTTON;
    downloadButtonAction =
      ProductsPermissions.READ_OTHER_COMPANY_LIBRARY_PRODUCT_DOWNLOAD_BUTTON;
    downloadSeparatelyButtonAction =
      ProductsPermissions.READ_OTHER_COMPANY_LIBRARY_PRODUCT_DOWNLOAD_SEPARATELY_BUTTON;
    editButtonAction =
      ProductsPermissions.READ_OTHER_COMPANY_EDIT_PRODUCT_BUTTON;
  }

  return (
    <div
      id="3d-stock-container"
      className="items-start p-4 md:p-8 flex flex-col gap-8 2xl:gap-x-11"
    >
      <ThreeDStockContentHeader
        tab={tab}
        categoryIds={defaultProductCategoryId?.in}
        changeItemsCategoryFilters={changeProductsCategoryFilters}
        isLibrary={isMyLibrary || !!companyNanoId}
      />

      <div
        id="3d-stock-wrapper"
        className="flex w-full gap-8 2xl:gap-x-11 items-start"
      >
        {showFilters && (
          <ThreeDStockProductsFilters
            teamUuid={teamUuid}
            changeProductsFilters={changeProductsFilters}
            filterProducts={filterProducts}
            defaultProductCategoryId={defaultProductCategoryId}
            defaultProductParentCategoryIds={defaultProductParentCategoryIds}
            productsFilters={productsFilters}
            sortProducts={sortProducts}
            closeFilters={closeFilters}
            clearFilters={clearProductsFilters}
            checkedCategories={checkedCategories}
            setCheckedCategories={setCheckedCategories}
            changeCategory={handleChangeCategory}
            withTypeFiler={withTypeFiler}
            withSeasonFilter={withSeasonFilter}
            isModal={isModal}
          />
        )}

        <div className="flex flex-col w-full gap-8 2xl:gap-x-11 min-w-0">
          {withSearchInImage && (
            <ThreeDStockContentSearchInImage
              changeProductsFilters={changeProductsFilters}
              view={searchInImageView}
              onDetectionsChange={handleDetectionsChange}
              activeDetectionId={activeDetectionId}
              onActiveDetectionChange={setActiveDetectionId}
            />
          )}

          <ThreeDStockContentSortAndView
            tab={tab}
            searchInImageView={
              isSearchingInImage ? searchInImageView : undefined
            }
            onSearchInImageViewChange={
              currentUser.hasPermissions(
                ProductsPermissions.READ_PRODUCTS_IMAGE_TOGGLE_VIEW_BUTTON
              )
                ? setSearchInImageView
                : undefined
            }
            detectedObjectsCount={detections?.length}
            currentSortI18nText={productsSortI18nTextTypes[sortBy]}
            sortButtons={sortButtons}
            totalCount={productsTotalCount}
            page={productsPage}
            limit={productsLimit}
            searchText={productsFilters.nameSkuTags}
            withOpenFiltersButton={!showFilters}
            withSelectedFilters={withFilters}
            openFilters={openFilters}
            withTotal={currentUser.hasPermissions(
              ProductsPermissions.READ_PRODUCTS_TOTAL_COUNT
            )}
            withImageScaleButton={currentUser.hasPermissions(
              ProductsPermissions.READ_PRODUCTS_IMAGE_SCALE
            )}
            withImageToggleViewButton={currentUser.hasPermissions(
              ProductsPermissions.READ_PRODUCTS_TOGGLE_VIEW_BUTTON
            )}
            withEditProductsButton={
              !isModal &&
              !companyNanoId &&
              !isMyLibrary &&
              currentUser.hasPermissions(
                ProductsPermissions.READ_PRODUCTS_EDIT_BUTTON
              )
            }
            withCompanyLibraryEditProductsButton={
              !isModal &&
              (isMyLibrary || !!companyNanoId) &&
              currentUser.hasPermissions(
                isMyLibrary
                  ? ProductsPermissions.READ_SELF_COMPANY_LIBRARY_EDIT_PRODUCTS_BUTTON
                  : ProductsPermissions.READ_OTHER_COMPANY_LIBRARY_EDIT_PRODUCTS_BUTTON
              )
            }
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
          />

          {isSearchingInImage && searchInImageView === 'all' ? (
            <LoadingGridSkeleton loaded={!!detections}>
              {detections && (
                <ThreeDStockAllObjectsProductsContent
                  key={JSON.stringify([
                    searchInImageParams?.imageUrl,
                    searchInImageParams?.lifestyleId,
                    initialFilters,
                    detections.map(({ id }) => id)
                  ])}
                  detections={detections}
                  activeDetectionId={activeDetectionId}
                  onActiveDetectionChange={setActiveDetectionId}
                  initialFilters={initialFilters}
                  lifestyleId={searchInImageParams?.lifestyleId}
                  cacheKey={productsCacheKey}
                  cellMinSize={scale}
                  onProductEditButtonMouseEnter={onProductEditButtonMouseEnter}
                  products={products}
                  productsError={productsError}
                  productsFetched={productsFetched}
                  productsIsPlaceholderData={productsIsPlaceholderData}
                  productsTotalCount={productsTotalCount}
                  hasNextProductsPage={hasNextProductsPage}
                  productsPage={productsPage}
                  productsLimit={productsLimit}
                  loadMoreProducts={loadMoreProducts}
                  productsFetchingNextPage={productsFetchingNextPage}
                  productsSet={productsSet}
                  updateProductsSetCache={updateProductsSetCache}
                  updateProductCache={updateProductCache}
                  productsSetCacheKeys={productsSetCacheKeys}
                  onProductSimilarSearchFilter={onProductSimilarSearchFilter}
                  companyNanoId={companyNanoId}
                  isMyLibrary={isMyLibrary}
                  changeProductsFilters={changeProductsFilters}
                  productsFilters={productsFilters}
                  downloadButtonAction={downloadButtonAction}
                  downloadSeparatelyButtonAction={
                    downloadSeparatelyButtonAction
                  }
                  findSimilarButtonAction={findSimilarButtonAction}
                  editButtonAction={editButtonAction}
                />
              )}
            </LoadingGridSkeleton>
          ) : isGridView ? (
            <ThreeDStockProductsList
              cellMinSize={scale}
              onProductEditButtonMouseEnter={onProductEditButtonMouseEnter}
              products={products}
              productsError={productsError}
              productsFetched={productsFetched}
              productsIsPlaceholderData={productsIsPlaceholderData}
              productsTotalCount={productsTotalCount}
              hasNextProductsPage={hasNextProductsPage}
              productsPage={productsPage}
              productsLimit={productsLimit}
              loadMoreProducts={loadMoreProducts}
              productsFetchingNextPage={productsFetchingNextPage}
              productsSet={productsSet}
              updateProductsSetCache={updateProductsSetCache}
              updateProductCache={updateProductCache}
              productsSetCacheKeys={productsSetCacheKeys}
              onProductSimilarSearchFilter={onProductSimilarSearchFilter}
              companyNanoId={companyNanoId}
              isMyLibrary={isMyLibrary}
              changeProductsFilters={changeProductsFilters}
              productsFilters={productsFilters}
              downloadButtonAction={downloadButtonAction}
              downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
              findSimilarButtonAction={findSimilarButtonAction}
              editButtonAction={editButtonAction}
            />
          ) : (
            <ThreeDStockProductsCompactList
              onProductEditButtonMouseEnter={onProductEditButtonMouseEnter}
              products={products}
              productsError={productsError}
              productsFetched={productsFetched}
              productsIsPlaceholderData={productsIsPlaceholderData}
              productsTotalCount={productsTotalCount}
              hasNextProductsPage={hasNextProductsPage}
              productsPage={productsPage}
              productsLimit={productsLimit}
              loadMoreProducts={loadMoreProducts}
              productsFetchingNextPage={productsFetchingNextPage}
              onProductSimilarSearchFilter={onProductSimilarSearchFilter}
              updateProductCache={updateProductCache}
              changeProductsFilters={changeProductsFilters}
              productsFilters={productsFilters}
              downloadButtonAction={downloadButtonAction}
              downloadSeparatelyButtonAction={downloadSeparatelyButtonAction}
              findSimilarButtonAction={findSimilarButtonAction}
              editButtonAction={editButtonAction}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ThreeDStockProductsContent;
